import React from "react";
import { useNavigate } from "react-router-dom";

// ─── Reusable sub-components ────────────────────────────────────────────────

function Badge({ children }) {
const navigate = useNavigate();

  return (
    <span className="inline-block bg-white/10 border border-white/20 text-blue-300 text-[11px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
      {children}
    </span>
  );
}

function AlertBox({ icon, children }) {
  return (
    <div className="flex gap-3 items-start bg-blue-50 border-l-4 border-blue-600 text-blue-900 rounded-[10px] p-4 mb-8 text-sm">
      <span className="text-base mt-0.5 shrink-0">{icon}</span>
      <div>{children}</div>
    </div>
  );
}

function SectionHeader({ number, title }) {
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-blue-100">
      <div className="bg-[#0F1F4B] text-white text-[11px] font-semibold w-[26px] h-[26px] rounded-full flex items-center justify-center shrink-0">
        {number}
      </div>
      <h2 className="font-serif font-normal text-[1.35rem] text-[#0F1F4B] tracking-tight">
        {title}
      </h2>
    </div>
  );
}

function Pill({ type }) {
  const styles = {
    yes:  "bg-emerald-50 text-emerald-700",
    no:   "bg-red-50 text-red-700",
    cond: "bg-amber-50 text-amber-800",
  };
  const labels = { yes: "Yes", no: "No", cond: "Limited" };
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full tracking-wide ${styles[type]}`}>
      {labels[type]}
    </span>
  );
}

function PillLabel({ type }) {
  const styles = {
    required: "bg-emerald-50 text-emerald-700",
    optional: "bg-amber-50 text-amber-800",
  };
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full tracking-wide ${styles[type]}`}>
      {type === "required" ? "Required" : "Optional"}
    </span>
  );
}

function HighlightCard({ children }) {
  return (
    <div className="bg-white border border-blue-100 rounded-[10px] px-6 py-5 my-4">
      {children}
    </div>
  );
}

function RightCard({ icon, title, description }) {
  return (
    <div className="bg-white border border-blue-100 rounded-[10px] p-4">
      <div className="text-xl mb-2">{icon}</div>
      <strong className="block text-sm font-semibold mb-1">{title}</strong>
      <p className="text-sm text-[#5A5F7A] m-0">{description}</p>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  return (
    <div className="font-sans bg-[#F8F9FE] text-[#1A1C2E] text-base leading-7 min-h-screen">
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap');
        .font-serif { font-family: 'DM Serif Display', serif; }
        body, .font-sans { font-family: 'DM Sans', sans-serif; }
        code { font-family: monospace; }
      `}</style>

      {/* ── Header ── */}
      <header className="bg-[#0F1F4B] text-white px-8 pt-12 pb-10 text-center">
        <Badge>App Privacy Policy</Badge>
        <h1 className="font-serif font-normal text-4xl md:text-5xl tracking-tight mb-2">
          Leeford
        </h1>
        <p className="text-sm text-white/60 mt-3">
          Effective Date: April 28, 2026 &nbsp;·&nbsp; Last Updated: April 28, 2026
        </p>
      </header>

      {/* ── Container ── */}
      <div className="max-w-[820px] mx-auto px-6 pt-10 pb-20">

        {/* Info Alert */}
        <AlertBox icon="ℹ">
          This Privacy Policy complies with Google Play's User Data Policy and applicable data
          protection laws. It fully discloses all data collected by the Leeford application,
          including precise location data and call activity logs used for field workforce management.
        </AlertBox>

        {/* ── Table of Contents ── */}
        <div className="bg-white border border-blue-100 rounded-xl px-7 py-6 mb-10">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-blue-600 mb-3">
            Table of Contents
          </p>
          <ol className="pl-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm list-decimal">
            {[
              ["#intro", "Introduction"],
              ["#data-collected", "Data We Collect"],
              ["#location", "Location Data — Full Disclosure"],
              ["#calls", "Call & Attendance Tracking"],
              ["#how-we-use", "How We Use Your Data"],
              ["#sharing", "Data Sharing"],
              ["#retention", "Data Retention"],
              ["#security", "Data Security"],
              ["#rights", "Your Rights"],
              ["#children", "Children's Privacy"],
              ["#changes", "Policy Changes"],
              ["#contact", "Contact Us"],
            ].map(([href, label]) => (
              <li key={href}>
                <a href={href} className="text-[#5A5F7A] no-underline hover:text-blue-600 transition-colors">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* ── Section 1: Introduction ── */}
        <section id="intro" className="mb-10">
          <SectionHeader number="1" title="Introduction" />
          <p className="text-[15px] text-[#2E3244] mb-3">
            Leeford ("the App", "we", "us", or "our") is a mobile field workforce management
            application. It is designed to help organisations track the daily working activities of
            their field employees, including visit calls, location-based check-ins, and attendance
            punching.
          </p>
          <p className="text-[15px] text-[#2E3244] mb-3">
            This Privacy Policy explains how we collect, use, store, and disclose information —
            including your precise location data and call activity — when you use the Leeford
            Android application available on Google Play.
          </p>
          <p className="text-[15px] text-[#2E3244] mb-3">
            By installing or using Leeford, you agree to the practices described in this policy.
            If you do not agree, please uninstall the application and discontinue use.
          </p>
        </section>

        {/* ── Section 2: Data We Collect ── */}
        <section id="data-collected" className="mb-10">
          <SectionHeader number="2" title="Data We Collect" />
          <p className="text-[15px] text-[#2E3244] mb-4">
            The following table summarises all categories of personal data collected by Leeford:
          </p>
          <div className="overflow-hidden rounded-[10px] border border-blue-100 my-4">
            <table className="w-full border-collapse text-sm bg-white">
              <thead>
                <tr>
                  {["Data Category", "Specific Data Points", "Collected", "Shared with Employer"].map((h) => (
                    <th key={h} className="bg-[#0F1F4B] text-white text-left px-3.5 py-2.5 font-medium text-[13px]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    cat: "Precise Location",
                    detail: "GPS coordinates, timestamp, accuracy radius at each check-in or call punch",
                    collected: "yes", shared: "yes",
                  },
                  {
                    cat: "Call / Visit Logs",
                    detail: "Customer/outlet visit records, call type, outcome, time, and geo-tag",
                    collected: "yes", shared: "yes",
                  },
                  {
                    cat: "Attendance",
                    detail: "Daily punch-in and punch-out time and location",
                    collected: "yes", shared: "yes",
                  },
                  {
                    cat: "Identity",
                    detail: "Full name, employee ID, mobile number, login credentials",
                    collected: "yes", shared: "cond",
                  },
                  {
                    cat: "Device Information",
                    detail: "Device model, OS version, app version, device identifiers",
                    collected: "yes", shared: "no",
                  },
                  {
                    cat: "App Usage",
                    detail: "Features accessed, session duration, error logs",
                    collected: "yes", shared: "no",
                  },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? "bg-[#F8F9FE]" : "bg-white"}>
                    <td className="px-3.5 py-2.5 border-b border-blue-50 align-top font-semibold text-[#2E3244]">
                      {row.cat}
                    </td>
                    <td className="px-3.5 py-2.5 border-b border-blue-50 align-top text-[#2E3244]">
                      {row.detail}
                    </td>
                    <td className="px-3.5 py-2.5 border-b border-blue-50 align-top">
                      <Pill type={row.collected} />
                    </td>
                    <td className="px-3.5 py-2.5 border-b border-blue-50 align-top">
                      <Pill type={row.shared} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Section 3: Location Data ── */}
        <section id="location" className="mb-10">
          <SectionHeader number="3" title="Location Data — Full Disclosure" />
          <AlertBox icon="📍">
            <strong>Location data is core to Leeford's functionality.</strong> This section fully
            discloses how location is accessed, collected, stored, and used, as required by Google
            Play's User Data Policy.
          </AlertBox>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">What location data we collect</h3>
          <p className="text-[15px] text-[#2E3244] mb-2">
            Leeford collects <strong>precise location data</strong> (GPS-level accuracy) from your device, including:
          </p>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li>Latitude and longitude coordinates</li>
            <li>Date and timestamp of each location reading</li>
            <li>Location accuracy radius reported by the device</li>
            <li>Location linked to each visit call, attendance punch, and field activity</li>
          </ul>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">When location data is collected</h3>
          <p className="text-[15px] text-[#2E3244] mb-2">
            Location is collected <strong>while the app is actively in use (foreground)</strong> during the following actions:
          </p>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li>Punching daily attendance (check-in and check-out)</li>
            <li>Logging a visit call or customer/outlet meeting</li>
            <li>Submitting field reports or daily work summaries</li>
            <li>Marking task completion at a field location</li>
          </ul>
          <p className="text-[15px] text-[#2E3244] mb-3">
            Location is <strong>not</strong> collected silently in the background when the app is closed.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">Why we collect location data</h3>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li><strong>Attendance verification:</strong> Confirming punch-in and punch-out occur at valid work locations</li>
            <li><strong>Visit call geo-tagging:</strong> Attaching verified location to each customer/outlet call log to confirm field presence</li>
            <li><strong>Daily work tracking:</strong> Allowing managers to review the field route and visits completed by each employee during the working day</li>
            <li><strong>Productivity reporting:</strong> Generating accurate daily, weekly, and monthly field activity reports for the organisation</li>
          </ul>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">Location permissions requested</h3>
          <div className="overflow-hidden rounded-[10px] border border-blue-100 my-4">
            <table className="w-full border-collapse text-sm bg-white">
              <thead>
                <tr>
                  {["Android Permission", "Purpose", "Required"].map((h) => (
                    <th key={h} className="bg-[#0F1F4B] text-white text-left px-3.5 py-2.5 font-medium text-[13px]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="px-3.5 py-2.5 border-b border-blue-50 align-top">
                    <code className="bg-[#EEF1FB] text-[#0F1F4B] px-1.5 py-0.5 rounded text-[13px]">ACCESS_FINE_LOCATION</code>
                  </td>
                  <td className="px-3.5 py-2.5 border-b border-blue-50 align-top text-[#2E3244]">
                    Precise GPS location for attendance punching and call geo-tagging
                  </td>
                  <td className="px-3.5 py-2.5 border-b border-blue-50 align-top">
                    <PillLabel type="required" />
                  </td>
                </tr>
                <tr className="bg-[#F8F9FE]">
                  <td className="px-3.5 py-2.5 align-top">
                    <code className="bg-[#EEF1FB] text-[#0F1F4B] px-1.5 py-0.5 rounded text-[13px]">ACCESS_COARSE_LOCATION</code>
                  </td>
                  <td className="px-3.5 py-2.5 align-top text-[#2E3244]">
                    Approximate location fallback when GPS signal is unavailable
                  </td>
                  <td className="px-3.5 py-2.5 align-top">
                    <PillLabel type="optional" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[15px] text-[#2E3244] mb-3">
            You will be prompted to grant location permission on first use. If permission is denied,
            attendance and call logging features will not function. You may revoke location permission
            at any time via your device's Settings.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">How location data is stored and transmitted</h3>
          <HighlightCard>
            <ul className="pl-5 list-disc text-[15px] text-[#2E3244] space-y-1">
              <li>All location data is transmitted to Leeford's servers via encrypted HTTPS connections</li>
              <li>Location records are stored on secure servers and linked to your employee account</li>
              <li>Your employer and organisation administrators can view your location-tagged attendance and call logs via the Leeford management dashboard</li>
              <li>We do not sell location data to any third party</li>
              <li>Location data is retained in accordance with your organisation's policy and applicable law (see Section 7)</li>
            </ul>
          </HighlightCard>
        </section>

        {/* ── Section 4: Call & Attendance ── */}
        <section id="calls" className="mb-10">
          <SectionHeader number="4" title="Call & Attendance Tracking" />
          <p className="text-[15px] text-[#2E3244] mb-3">
            Leeford records field employee <strong>visit calls</strong> (in-person customer or outlet
            visits) and <strong>daily attendance punching</strong> as part of its core workforce
            management function. This is distinct from telephonic call logs — Leeford does not access
            your phone's call history, SMS, or contacts.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">What is tracked</h3>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li><strong>Visit calls:</strong> Each field visit logged by the employee — including outlet/customer name, visit time, call outcome, and GPS location at the time of logging</li>
            <li><strong>Daily attendance:</strong> Punch-in time and location at start of work day; punch-out time and location at end of work day</li>
            <li><strong>Daily work summary:</strong> Number of calls made, distance travelled (derived from logged locations), and tasks completed</li>
          </ul>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">Who can see this data</h3>
          <p className="text-[15px] text-[#2E3244] mb-2">This information is visible to:</p>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li>The field employee themselves (their own records)</li>
            <li>Their direct reporting manager</li>
            <li>Authorised organisation administrators via the Leeford dashboard</li>
          </ul>
          <p className="text-[15px] text-[#2E3244]">
            Leeford does not access or read your device's native phone call logs, contacts, SMS
            messages, or any telephony data.
          </p>
        </section>

        {/* ── Section 5: How We Use ── */}
        <section id="how-we-use" className="mb-10">
          <SectionHeader number="5" title="How We Use Your Data" />
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li>To provide core features: attendance tracking, visit call logging, and field activity reporting</li>
            <li>To make location-tagged records available to authorised managers and administrators</li>
            <li>To generate daily, weekly, and monthly productivity reports for your organisation</li>
            <li>To authenticate users and maintain account security</li>
            <li>To diagnose technical issues and improve app performance</li>
            <li>To send work-related notifications and task reminders</li>
            <li>To comply with applicable legal or regulatory requirements</li>
          </ul>
          <p className="text-[15px] text-[#2E3244]">
            We do not use your data for targeted advertising. We do not build advertising profiles
            from your personal or location data.
          </p>
        </section>

        {/* ── Section 6: Data Sharing ── */}
        <section id="sharing" className="mb-10">
          <SectionHeader number="6" title="Data Sharing and Disclosure" />

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">With your organisation</h3>
          <p className="text-[15px] text-[#2E3244] mb-3">
            Your employer or the organisation that has deployed Leeford has access to your location
            data, visit call logs, and attendance records through the administrative dashboard. This
            sharing is the core purpose of the App.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">With service providers</h3>
          <p className="text-[15px] text-[#2E3244] mb-3">
            We engage trusted third-party service providers (e.g., cloud hosting, infrastructure) who
            process data strictly on our behalf under data processing agreements and confidentiality
            obligations.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">For legal compliance</h3>
          <p className="text-[15px] text-[#2E3244] mb-3">
            We may disclose data if required by applicable law, court order, or to protect the rights,
            safety, or property of our users or the public.
          </p>

          <h3 className="text-[15px] font-semibold text-[#1A1C2E] mt-5 mb-2">We will never</h3>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] space-y-1">
            <li>Sell your personal or location data to third parties</li>
            <li>Share your data with advertisers or data brokers</li>
            <li>Use your location for purposes beyond those stated in this policy</li>
          </ul>
        </section>

        {/* ── Section 7: Retention ── */}
        <section id="retention" className="mb-10">
          <SectionHeader number="7" title="Data Retention" />
          <p className="text-[15px] text-[#2E3244] mb-3">
            We retain your personal data — including location, attendance, and call logs — for as long
            as your account is active or as required by your organisation's data retention policy.
          </p>
          <p className="text-[15px] text-[#2E3244] mb-3">
            On account deletion or termination of employment, personal data is deleted from active
            systems within 30 days, unless retention is required by applicable law.
          </p>
          <p className="text-[15px] text-[#2E3244]">
            Anonymised or aggregated data may be retained longer for statistical and analytical purposes.
          </p>
        </section>

        {/* ── Section 8: Security ── */}
        <section id="security" className="mb-10">
          <SectionHeader number="8" title="Data Security" />
          <p className="text-[15px] text-[#2E3244] mb-3">
            We implement appropriate technical and organisational safeguards to protect your data:
          </p>
          <ul className="pl-5 list-disc text-[15px] text-[#2E3244] mb-3 space-y-1">
            <li>Encrypted data transmission using HTTPS/TLS</li>
            <li>Secure cloud infrastructure with strict access controls</li>
            <li>Role-based permissions limiting who within your organisation can view your records</li>
            <li>Regular security monitoring and reviews</li>
          </ul>
          <p className="text-[15px] text-[#2E3244]">
            No data transmission over the internet is 100% guaranteed to be secure. We encourage you
            to use a strong password and keep your device secure.
          </p>
        </section>

        {/* ── Section 9: Rights ── */}
        <section id="rights" className="mb-10">
          <SectionHeader number="9" title="Your Rights" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-4">
            {[
              { icon: "👁", title: "Access", desc: "Request a copy of the personal data we hold about you." },
              { icon: "✏️", title: "Correction", desc: "Request correction of inaccurate or incomplete records." },
              { icon: "🗑", title: "Deletion", desc: "Request deletion of your data, subject to legal obligations." },
              { icon: "🚫", title: "Withdraw Consent", desc: "Revoke location permission anytime via device Settings." },
              { icon: "📦", title: "Portability", desc: "Request your data in a structured, portable format." },
              { icon: "⚠️", title: "Complaint", desc: "Lodge a complaint with your local data protection authority." },
            ].map((r) => (
              <RightCard key={r.title} icon={r.icon} title={r.title} description={r.desc} />
            ))}
          </div>
          <p className="text-[15px] text-[#2E3244] mt-4">
            To exercise any of these rights, contact us at the details in Section 12.
          </p>
        </section>

        {/* ── Section 10: Children ── */}
        <section id="children" className="mb-10">
          <SectionHeader number="10" title="Children's Privacy" />
          <p className="text-[15px] text-[#2E3244]">
            Leeford is intended exclusively for working adults. It is not directed at children under
            the age of 13 (or 16 in the European Economic Area). We do not knowingly collect personal
            data from minors. If we become aware that a minor has provided data, we will delete it
            promptly.
          </p>
        </section>

        {/* ── Section 11: Changes ── */}
        <section id="changes" className="mb-10">
          <SectionHeader number="11" title="Changes to This Policy" />
          <p className="text-[15px] text-[#2E3244] mb-3">
            We may update this Privacy Policy from time to time. When material changes are made, we
            will notify users via an in-app notification or by updating the "Last Updated" date at the
            top of this page.
          </p>
          <p className="text-[15px] text-[#2E3244]">
            Continued use of Leeford after changes are published constitutes acceptance of the revised
            policy.
          </p>
        </section>

        {/* ── Section 12: Contact ── */}
        <section id="contact" className="mb-10">
          <SectionHeader number="12" title="Contact Us" />
          <p className="text-[15px] text-[#2E3244] mb-4">
            For any questions, concerns, or data requests related to this Privacy Policy, please reach
            out:
          </p>
          <HighlightCard>
            <p className="text-[15px] text-[#2E3244]">
              <strong>Leeford</strong><br />
              App: Leeford<br />
              Email:{" "}
              <a href="mailto:app@leeford.in" className="text-blue-600 no-underline hover:underline">
                app@leeford.in
              </a>
              <br />
              Website:{" "}
              <a
                href="https://www.leeford.in"
                className="text-blue-600 no-underline hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                www.leeford.in
              </a>
            </p>
            <p className="mt-3 text-sm text-[#5A5F7A]">
              We aim to respond to all privacy inquiries within 7 business days.
            </p>
          </HighlightCard>
        </section>
      </div>

      {/* ── Footer ── */}
      <footer className="text-center px-8 py-8 text-[13px] text-[#5A5F7A] border-t border-blue-100 bg-white">
        © 2026 Leeford. All rights reserved. &nbsp;·&nbsp; Privacy Policy &nbsp;·&nbsp; Effective April 28, 2026
      </footer>
    </div>
  );
}